import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { TabStrip } from '@shared-ui/components';
import { sessionsApi } from '../../../api/siteAnalyticsApi';
import CampaignsPanel from './CampaignsPanel';
import LandingSessionsTable from '../shared/LandingSessionsTable';
import SiteAcquisition from './SiteAcquisition';
import SegmentsPanel from './SegmentsPanel';

type AcquisitionTab = 'sessions' | 'arrivals' | 'campaigns' | 'segments';
const ACQUISITION_TABS = [
  { key: 'sessions' as const, label: 'All sessions' },
  { key: 'arrivals' as const, label: 'Arrivals & accounts' },
  { key: 'campaigns' as const, label: 'Campaigns' },
  { key: 'segments' as const, label: 'Segments' },
];

/** Arrivals and accounts on either property, and the segment definitions its
 *  funnels are asked about — neither belongs to one property. */
export function AcquisitionReport({ from, to }: { from: Date; to: Date }) {
  const [tab, setTab] = useState<AcquisitionTab>('sessions');
  return (
    <>
      <TabStrip tabs={ACQUISITION_TABS} active={tab} onChange={setTab} ariaLabel="Acquisition views" className="sa-tabs" />
      {tab === 'arrivals' && <SiteAcquisition from={from} to={to} />}
      {tab === 'sessions' && <AllSessions from={from} to={to} />}
      {tab === 'campaigns' && <CampaignsPanel from={from} to={to} />}
      {tab === 'segments' && <SegmentsPanel />}
    </>
  );
}

/**
 * Every session on both properties, one row each, with the account's first
 * touch — the raw arrivals under the acquisition figures. Its filters are its
 * own: nothing above narrows it.
 */
function AllSessions({ from, to }: { from: Date; to: Date }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['sessions', from.toISOString(), to.toISOString()],
    queryFn: () => sessionsApi.get(from, to),
  });
  if (isLoading) return <p className="sa-empty">Loading sessions…</p>;
  if (error) return <p className="sa-error">{(error as Error).message}</p>;
  return data
    ? <LandingSessionsTable sessions={data.sessions} truncated={data.sessionsTruncated} from={from} to={to} />
    : null;
}
